/* AtomiQ fx layer (v4 round 2): the shared motion and depth hooks. ES module, no dependencies.
   Contract: docs/v4/design-system.md section 16 (and ronde2.md, "The shared fx API").

     data-fx-tilt[="6"]        pointer tilt (max degrees) with --fx-rx, --fx-ry, --fx-x, --fx-y, --fx-on for a sheen
     data-fx-magnet[="0.25"]   primary buttons lean towards the pointer
     data-fx-count             a number counts up from 0 (or data-fx-from) to its own text when it enters (900 ms)
     data-fx-draw              strokes with pathLength draw in when the SVG (or container) enters
     data-fx-stagger           the direct children enter one by one (opacity and a 16 px rise)
                               (draw and stagger may sit on the same element; anything already on screen enters at once)
     data-fx-light             a soft light follows the pointer over a graphite section and reveals the lattice
     data-aq-loop              an endless CSS animation inside runs only while the element is near the screen
                               (.fx-loop and .is-running; theme.css pauses it otherwise)

   One easing family: expo out for entrances (CSS --ease-out), a critically damped spring for everything that follows
   the pointer. Everything stops and shows its final state under reduced motion and with the site-wide motion switch
   (html.is-motion-paused). Nothing is hidden before this file runs: elements are only armed by JavaScript.
   <aq-waves> lives in waves.js, imported here only when a page has one. */

const AQ = (window.AQ = window.AQ || {});
const root = document.documentElement;
const reducedQuery = matchMedia('(prefers-reduced-motion: reduce)');
const fineQuery = matchMedia('(hover: hover) and (pointer: fine)');
const $$ = (selector, scope = document) => {
  const list = [...scope.querySelectorAll(selector)];
  if (scope instanceof Element && scope.matches(selector)) list.unshift(scope);
  return list;
};
const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));

const isStill = () => reducedQuery.matches || root.classList.contains('is-motion-paused');

/* ---------------------------------------------------------------- springs (pointer-driven values) */

/* A critically damped spring per value; one shared frame loop runs only while something is moving. */
const movers = new Set();
let frameId = 0;
let lastT = 0;

function tick(now) {
  const dt = Math.min(0.05, (now - (lastT || now)) / 1000) || 1 / 60;
  lastT = now;
  for (const mover of movers) {
    if (!mover.step(dt)) movers.delete(mover);
  }
  frameId = movers.size ? requestAnimationFrame(tick) : 0;
  if (!frameId) lastT = 0;
}

function wake(mover) {
  movers.add(mover);
  if (!frameId) frameId = requestAnimationFrame(tick);
}

/* Moves value towards target with stiffness k (per second); returns true while still moving. */
function spring(state, key, target, dt, k = 14) {
  const v = state[key];
  const vel = state[`${key}V`] || 0;
  const accel = k * k * (target - v) - 2 * k * vel;
  const nextVel = vel + accel * dt;
  const next = v + nextVel * dt;
  state[`${key}V`] = nextVel;
  state[key] = next;
  return Math.abs(target - next) > 0.0005 || Math.abs(nextVel) > 0.0005;
}

/* ---------------------------------------------------------------- tilt and sheen */

class Tilt {
  constructor(el) {
    this.el = el;
    const raw = parseFloat(el.dataset.fxTilt);
    this.max = Number.isFinite(raw) ? clamp(raw, 0, 12) : 6;
    this.s = { x: 0.5, y: 0.5, on: 0 };
    this.t = { x: 0.5, y: 0.5, on: 0 };
    this.onMove = (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      const box = el.getBoundingClientRect();
      this.t.x = clamp((event.clientX - box.left) / box.width);
      this.t.y = clamp((event.clientY - box.top) / box.height);
      this.t.on = 1;
      wake(this);
    };
    this.onLeave = () => {
      this.t.x = 0.5;
      this.t.y = 0.5;
      this.t.on = 0;
      wake(this);
    };
    el.addEventListener('pointerenter', this.onMove, { passive: true });
    el.addEventListener('pointermove', this.onMove, { passive: true });
    el.addEventListener('pointerleave', this.onLeave, { passive: true });
    el.classList.add('fx-tilt');
  }

  step(dt) {
    if (isStill()) {
      this.reset();
      return false;
    }
    let moving = spring(this.s, 'x', this.t.x, dt, 11);
    moving = spring(this.s, 'y', this.t.y, dt, 11) || moving;
    moving = spring(this.s, 'on', this.t.on, dt, 9) || moving;
    this.paint();
    return moving;
  }

  paint() {
    const { x, y, on } = this.s;
    const style = this.el.style;
    style.setProperty('--fx-x', x.toFixed(4));
    style.setProperty('--fx-y', y.toFixed(4));
    style.setProperty('--fx-on', clamp(on).toFixed(4));
    style.setProperty('--fx-rx', ((0.5 - y) * 2 * this.max * on).toFixed(3));
    style.setProperty('--fx-ry', ((x - 0.5) * 2 * this.max * on).toFixed(3));
  }

  reset() {
    Object.assign(this.s, { x: 0.5, y: 0.5, on: 0, xV: 0, yV: 0, onV: 0 });
    ['--fx-x', '--fx-y', '--fx-on', '--fx-rx', '--fx-ry'].forEach((name) => this.el.style.removeProperty(name));
  }

  destroy() {
    this.el.removeEventListener('pointerenter', this.onMove);
    this.el.removeEventListener('pointermove', this.onMove);
    this.el.removeEventListener('pointerleave', this.onLeave);
    this.reset();
  }
}

/* ---------------------------------------------------------------- magnet */

const magnets = new Set();
let pointer = null;

class Magnet {
  constructor(el) {
    this.el = el;
    this.k = clamp(parseFloat(el.dataset.fxMagnet) || 0.25, 0.05, 0.6);
    this.s = { x: 0, y: 0 };
    this.t = { x: 0, y: 0 };
    el.classList.add('fx-magnet');
    magnets.add(this);
  }

  aim() {
    if (!pointer || isStill()) {
      this.t.x = 0;
      this.t.y = 0;
      return;
    }
    const box = this.el.getBoundingClientRect();
    const reach = 28;
    const inside = pointer.x > box.left - reach && pointer.x < box.right + reach && pointer.y > box.top - reach && pointer.y < box.bottom + reach;
    if (!inside) {
      this.t.x = 0;
      this.t.y = 0;
      return;
    }
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    this.t.x = clamp((pointer.x - cx) * this.k, -12, 12);
    this.t.y = clamp((pointer.y - cy) * this.k, -7, 7);
  }

  step(dt) {
    if (isStill()) {
      this.reset();
      return false;
    }
    let moving = spring(this.s, 'x', this.t.x, dt, 16);
    moving = spring(this.s, 'y', this.t.y, dt, 16) || moving;
    this.el.style.setProperty('--fx-mx', `${this.s.x.toFixed(2)}px`);
    this.el.style.setProperty('--fx-my', `${this.s.y.toFixed(2)}px`);
    return moving;
  }

  reset() {
    Object.assign(this.s, { x: 0, y: 0, xV: 0, yV: 0 });
    this.el.style.removeProperty('--fx-mx');
    this.el.style.removeProperty('--fx-my');
  }

  destroy() {
    magnets.delete(this);
    this.reset();
  }
}

let aimQueued = false;
function onPointerMove(event) {
  if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
  pointer = { x: event.clientX, y: event.clientY };
  if (aimQueued || !magnets.size) return;
  aimQueued = true;
  requestAnimationFrame(() => {
    aimQueued = false;
    for (const magnet of magnets) {
      if (!magnet.el.isConnected) {
        magnet.destroy();
        continue;
      }
      const before = magnet.t.x + magnet.t.y;
      magnet.aim();
      if (before !== magnet.t.x + magnet.t.y || magnet.s.x || magnet.s.y) wake(magnet);
    }
  });
}

/* ---------------------------------------------------------------- entrances (one observer) */

/* One element can carry several entrances (data-fx-stagger and data-fx-draw together), so each element keeps a
   list of callbacks and every one of them runs when it enters. */
const entering = new Map();

function enter(el) {
  const runs = entering.get(el);
  entering.delete(el);
  enterObserver?.unobserve(el);
  runs?.forEach((run) => run());
}

const enterObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) enter(entry.target);
          });
        },
        /* Edge-based: an element that arrives by scrolling enters when its top passes 88 % of the screen. */
        { rootMargin: '0px 0px -12% 0px', threshold: 0 }
      )
    : null;

/* Elements already on the screen when they are armed (the first screen, a result that lands in view) enter at
   once, including those in the bottom band the observer margin leaves out. Two frames, so the armed state is
   painted first and the entrance plays. */
let firstQueued = false;
const firstScreen = new Set();
function enterVisibleSoon(el) {
  firstScreen.add(el);
  if (firstQueued) return;
  firstQueued = true;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      firstQueued = false;
      const list = [...firstScreen];
      firstScreen.clear();
      list.forEach((item) => {
        if (!entering.has(item) || !item.isConnected) return;
        const box = item.getBoundingClientRect();
        if (box.bottom > 0 && box.top < innerHeight && (box.width || box.height)) enter(item);
      });
    })
  );
}

function onEnter(el, run) {
  if (!enterObserver) {
    run();
    return;
  }
  const runs = entering.get(el);
  if (runs) runs.push(run);
  else {
    entering.set(el, [run]);
    enterObserver.observe(el);
  }
  enterVisibleSoon(el);
  watchPassed();
}

/* An anchor jump or a fast fling can carry an element past the screen between two observer checks; it never
   "intersects", so a check when the scroll comes to rest lets everything that is in view or already passed enter
   as well. One listener, added once and removed when nothing waits any more: scrollend where the browser has it,
   otherwise a scroll listener throttled to one check per 120 ms. */
const hasScrollEnd = 'onscrollend' in window;
let passedOn = false;
let passedTimer = 0;

function checkPassed() {
  const line = innerHeight * 0.88;
  [...entering.keys()].forEach((el) => {
    if (!el.isConnected) {
      entering.delete(el);
      return;
    }
    if (el.getBoundingClientRect().top < line) enter(el);
  });
  if (!entering.size) unwatchPassed();
}

function onPassedEnd() {
  requestAnimationFrame(checkPassed);
}

function onPassedScroll() {
  if (passedTimer) return;
  passedTimer = setTimeout(() => {
    passedTimer = 0;
    requestAnimationFrame(checkPassed);
  }, 120);
}

function watchPassed() {
  if (passedOn || !entering.size) return;
  passedOn = true;
  if (hasScrollEnd) addEventListener('scrollend', onPassedEnd, { passive: true });
  else addEventListener('scroll', onPassedScroll, { passive: true });
}

function unwatchPassed() {
  passedOn = false;
  removeEventListener('scrollend', onPassedEnd);
  removeEventListener('scroll', onPassedScroll);
  clearTimeout(passedTimer);
  passedTimer = 0;
}

/* The duration of the longest entrance, after which the armed classes are removed so nothing lingers. */
const ENTER_MS = 1100;

/* One timer per entrance kind, so a stagger and a draw on the same element each clean up their own classes. */
function settleLater(el, key, classes, ms) {
  el.fxSettle ||= {};
  clearTimeout(el.fxSettle[key]);
  el.fxSettle[key] = setTimeout(() => {
    delete el.fxSettle[key];
    el.classList.remove(...classes);
  }, ms);
}

/* data-fx-stagger: the direct children enter one by one. State classes: fx-armed, then fx-in. */
function armStagger(el) {
  const kids = [...el.children].filter((kid) => !kid.hidden);
  kids.forEach((kid, i) => kid.style.setProperty('--fx-i', String(i)));
  el.classList.add('fx-armed');
  onEnter(el, () => {
    el.classList.add('fx-in');
    settleLater(el, 'stagger', ['fx-armed', 'fx-in'], ENTER_MS + kids.length * 70);
  });
}

/* data-fx-draw: strokes with pathLength (the icon set has it on every stroke) draw in. Shapes without pathLength
   and without a dash pattern of their own get pathLength="1"; dashed lines and fills keep their look and fade in. */
const SHAPES = 'path, line, polyline, polygon, circle, ellipse, rect';
function armDraw(el) {
  const shapes = $$(SHAPES, el).filter((shape) => shape.closest('svg') && !shape.closest('defs, symbol, clipPath, mask, pattern'));
  if (!shapes.length) return;
  /* The delay steps per icon when the element holds several (a list of icons draws icon by icon, every stroke of
     one icon together, at most 10 steps), and per stroke inside one drawing (a schematic, at most 24 steps). So a
     row of icons that appears late is complete within about 1.5 s. */
  const svgs = [...new Set(shapes.map((shape) => shape.closest('svg')))];
  const perIcon = svgs.length > 1;
  let last = 0;
  shapes.forEach((shape, i) => {
    if (!shape.hasAttribute('pathLength')) {
      const style = getComputedStyle(shape);
      const stroked = style.stroke && style.stroke !== 'none';
      const dashed = style.strokeDasharray && style.strokeDasharray !== 'none';
      if (stroked && !dashed) shape.setAttribute('pathLength', '1');
      else shape.classList.add('fx-fade');
    }
    const step = perIcon ? Math.min(svgs.indexOf(shape.closest('svg')), 10) : Math.min(i, 24);
    last = Math.max(last, step);
    shape.style.setProperty('--fx-i', String(step));
  });
  /* Its own state classes (fx-draw-armed, then fx-drawn), so it combines with data-fx-stagger on one element. */
  el.classList.add('fx-draw-armed');
  onEnter(el, () => {
    el.classList.add('fx-drawn');
    settleLater(el, 'draw', ['fx-draw-armed', 'fx-drawn'], ENTER_MS + last * 45 + 200);
  });
}

/* data-fx-count: counts up to the number in the element's own text ("€45,98", "250 ml", "4"), keeping prefix,
   suffix, child elements and the locale's decimal comma. Screen readers read the final text throughout. */
const NUMBER = /(\d{1,3}(?:[.\s\u00a0]\d{3})+|\d+)(?:([,.])(\d+))?/;
function numberNode(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (NUMBER.test(node.data) && !node.parentElement.closest('.sr-only')) return node;
  }
  return null;
}

function armCount(el) {
  const node = numberNode(el);
  if (!node) return;
  const text = node.data;
  const match = text.match(NUMBER);
  const [whole, intPart, decSep, decPart = ''] = match;
  const groupSep = (intPart.match(/[.\s\u00a0]/) || [''])[0];
  const to = Number(intPart.replace(/[.\s\u00a0]/g, '') + (decPart ? `.${decPart}` : ''));
  const from = Number(String(el.dataset.fxFrom || '0').replace(',', '.')) || 0;
  if (!Number.isFinite(to) || to === from) return;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + whole.length);
  const digits = decPart.length;
  const format = (value) => {
    const [i, d] = value.toFixed(digits).split('.');
    const int = groupSep ? i.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : i;
    return d ? `${int}${decSep || ','}${d}` : int;
  };
  el.classList.add('fx-count');
  onEnter(el, () => {
    if (isStill() || !node.isConnected) return;
    const run = document.createElement('span');
    run.className = 'fx-count__run';
    run.setAttribute('aria-hidden', 'true');
    const quiet = document.createElement('span');
    quiet.className = 'sr-only';
    quiet.textContent = text;
    node.replaceWith(run, quiet);
    const restore = () => {
      if (run.isConnected) {
        run.replaceWith(node);
        quiet.remove();
      }
    };
    /* Cubic out over 900 ms, and the final figure as soon as less than one unit of the last digit is left: an
       expo tail would sit on "249 ml" for almost half a second, which reads as a wrong product fact. */
    const ms = Number(el.dataset.fxMs) || 900;
    const unit = Math.pow(10, -digits);
    const start = performance.now();
    const step = (now) => {
      const t = clamp((now - start) / ms);
      let value = from + (to - from) * (1 - Math.pow(1 - t, 3));
      if (Math.abs(to - value) < unit) value = to;
      run.textContent = `${before}${format(value)}${after}`;
      if (t < 1 && !isStill()) requestAnimationFrame(step);
      else restore();
    };
    run.textContent = `${before}${format(from)}${after}`;
    requestAnimationFrame(step);
  });
}

/* ---------------------------------------------------------------- cursor light (graphite sections) */

const lights = new Set();
class Light {
  constructor(section) {
    this.section = section;
    this.layer = document.createElement('span');
    this.layer.className = 'fx-light';
    this.layer.setAttribute('aria-hidden', 'true');
    section.classList.add('fx-light-host');
    section.prepend(this.layer);
    this.s = { x: 0, y: 0, on: 0 };
    this.t = { x: 0, y: 0, on: 0 };
    this.first = true;
    this.onMove = (event) => {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      const box = section.getBoundingClientRect();
      this.t.x = event.clientX - box.left;
      this.t.y = event.clientY - box.top;
      this.t.on = 1;
      if (this.first) {
        this.s.x = this.t.x;
        this.s.y = this.t.y;
        this.first = false;
      }
      wake(this);
    };
    this.onLeave = () => {
      this.t.on = 0;
      wake(this);
    };
    section.addEventListener('pointermove', this.onMove, { passive: true });
    section.addEventListener('pointerleave', this.onLeave, { passive: true });
    lights.add(this);
  }

  step(dt) {
    if (isStill()) {
      this.s.on = 0;
      this.layer.style.opacity = '0';
      return false;
    }
    let moving = spring(this.s, 'x', this.t.x, dt, 7);
    moving = spring(this.s, 'y', this.t.y, dt, 7) || moving;
    moving = spring(this.s, 'on', this.t.on, dt, 5) || moving;
    const style = this.layer.style;
    style.setProperty('--fx-lx', `${this.s.x.toFixed(1)}px`);
    style.setProperty('--fx-ly', `${this.s.y.toFixed(1)}px`);
    style.opacity = clamp(this.s.on).toFixed(3);
    return moving;
  }

  destroy() {
    this.section.removeEventListener('pointermove', this.onMove);
    this.section.removeEventListener('pointerleave', this.onLeave);
    this.layer.remove();
    this.section.classList.remove('fx-light-host');
    lights.delete(this);
  }
}

/* ---------------------------------------------------------------- endless loops only on screen */

const loopObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => entry.target.classList.toggle('is-running', entry.isIntersecting));
        },
        { rootMargin: '10% 0px' }
      )
    : null;

function armLoop(el) {
  el.classList.add('fx-loop');
  if (loopObserver) loopObserver.observe(el);
  else el.classList.add('is-running');
}

/* ---------------------------------------------------------------- init */

const tilts = new WeakMap();

function init(scope = document) {
  const still = isStill();
  const fine = fineQuery.matches;

  $$('[data-fx-stagger]:not(.fx-stagger-done)', scope).forEach((el) => {
    el.classList.add('fx-stagger-done');
    if (!still) armStagger(el);
  });
  $$('[data-fx-draw]:not(.fx-draw-done)', scope).forEach((el) => {
    el.classList.add('fx-draw-done');
    if (!still) armDraw(el);
  });
  $$('[data-fx-count]:not(.fx-count)', scope).forEach((el) => {
    if (!still) armCount(el);
  });
  $$('[data-aq-loop]:not(.fx-loop)', scope).forEach(armLoop);

  if (fine) {
    $$('[data-fx-tilt]', scope).forEach((el) => {
      if (!tilts.has(el)) tilts.set(el, new Tilt(el));
    });
    $$('[data-fx-magnet]:not(.fx-magnet)', scope).forEach((el) => new Magnet(el));
    $$('[data-fx-light]:not(.fx-light-host)', scope).forEach((el) => new Light(el));
  }

  root.classList.add('fx-ready');

  if (document.querySelector('aq-waves') && !customElements.get('aq-waves') && AQ.fxWaves) {
    import(AQ.fxWaves).catch(() => document.querySelectorAll('aq-waves').forEach((el) => el.setAttribute('data-state', 'static')));
  }
}

/* Finish every entrance at once (motion switched off): nothing may stay hidden. */
function finishAll() {
  document.querySelectorAll('.fx-armed, .fx-draw-armed').forEach((el) => el.classList.remove('fx-armed', 'fx-draw-armed', 'fx-in', 'fx-drawn'));
  entering.forEach((runs, el) => enterObserver?.unobserve(el));
  entering.clear();
  firstScreen.clear();
  movers.forEach((mover) => mover.reset?.());
  movers.clear();
}

function onMotion() {
  root.classList.toggle('fx-on', !isStill());
  if (isStill()) finishAll();
}

AQ.fx = {
  init,
  get still() {
    return isStill();
  },
  /* A critically damped spring helper for section scripts: AQ.fx.spring(state, 'x', target, dt, k). */
  spring,
  /* Replays the count of one element (for numbers that change, such as a meter). */
  count(el) {
    if (!el || isStill()) return;
    el.classList.remove('fx-count');
    armCount(el);
  },
};

root.classList.toggle('fx-on', !isStill());
addEventListener('pointermove', onPointerMove, { passive: true });
reducedQuery.addEventListener?.('change', onMotion);
document.addEventListener('aq:motion', onMotion);
document.addEventListener('shopify:section:load', (event) => init(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  lights.forEach((light) => {
    if (event.target.contains(light.section)) light.destroy();
  });
});

/* Markup that arrives later (a finder result, a re-rendered cart) is armed as it lands. */
const HOOKS = '[data-fx-stagger], [data-fx-draw], [data-fx-count], [data-fx-tilt], [data-fx-magnet], [data-fx-light], [data-aq-loop], aq-waves';
if ('MutationObserver' in window) {
  let queued = false;
  const pending = new Set();
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && !node.classList?.contains('fx-light') && !node.classList?.contains('fx-count__run')) pending.add(node);
      }
    }
    if (queued || !pending.size) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const nodes = [...pending];
      pending.clear();
      nodes.forEach((node) => {
        if (node.isConnected && (node.matches?.(HOOKS) || node.querySelector?.(HOOKS))) init(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();
